<!-- BottomNavItem.vue -->
<!-- Descripción: Botón presentacional de un ítem del pill (fijo o el slot dinámico). -->
<!-- Autor: Claude -->
<!-- Fecha: 2026-09-26 -->

<script setup lang="ts">
import type { FunctionalComponent } from 'vue'

const props = withDefaults(
  defineProps<{
    label: string
    icon: FunctionalComponent
    active: boolean
    ariaLabel?: string
  }>(),
  { ariaLabel: undefined }
)

const emit = defineEmits<{ click: [] }>()
</script>

<template>
  <button
    type="button"
    :aria-label="props.ariaLabel ?? props.label"
    :aria-current="props.active ? 'page' : undefined"
    :title="props.label"
    class="nav-item relative isolate flex min-h-[2.75rem] min-w-[3.5rem] flex-col items-center justify-center gap-0.5 rounded-2xl transition-colors"
    :class="props.active ? 'nav-item--active text-light-accent dark:text-white' : 'text-light-text-primary/80 dark:text-white/75'"
    @click="emit('click')"
  >
    <component :is="props.icon" class="nav-icon h-6 w-6 shrink-0" />
    <span class="nav-label w-full truncate text-center">{{ props.label }}</span>
  </button>
</template>

<style scoped>
/* Activo: tinte de acento por encima de la almohada (el fondo del botón quedaría debajo de ella). */
.nav-item--active::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  pointer-events: none;
  background: rgb(140 90 61 / 0.14);
}
.dark .nav-item--active::after { background: rgb(140 90 61 / 0.45); }

.nav-label {
  font-size: 10px;
  font-weight: 600;
  /* line-height: 1 recortaba la "g" de "Agenda" (el botón tiene sobra bajo min-h-[2.75rem],
     así que esto no cambia el alto visible de la barra). */
  line-height: 1.3;
  letter-spacing: 0.01em;
  text-shadow: 0 0 6px rgb(255 255 255 / 0.6);
}

/* Halo en vez de fondo opaco: el vidrio queda transparente y el texto se sigue leyendo. */
.nav-icon { filter: drop-shadow(0 0 3px rgb(255 255 255 / 0.6)); }
.dark .nav-label { text-shadow: 0 1px 4px rgb(0 0 0 / 0.6); }
.dark .nav-icon { filter: drop-shadow(0 1px 3px rgb(0 0 0 / 0.6)); }

@media (prefers-reduced-transparency: reduce) {
  .nav-label,
  .dark .nav-label { text-shadow: none; }
  .nav-icon,
  .dark .nav-icon { filter: none; }
}
</style>
